import { createErrorClass } from '@canvas/error';
import { UserTypes, type WhiteboardSessionData } from '@canvas/frame-api-types';
import { ANONYMOUS_ID } from '@canvas/model/identity';

import { getContentAPI } from './host-api/host-frame-context';

export const TenantIdMissingError = createErrorClass('TenantIdMissingError');
export const ActivationIdMissingError = createErrorClass('ActivationIdMissingError');
export const NoSessionDataError = createErrorClass('NoSessionDataError');

export function getSessionDataUnvalidated() {
  const contentAPI = getContentAPI();
  // While the content API should always be defined, technically it is possible (eg if embedding outside
  // of confluence) that this is not the case. In this case, we cannot proceed with the app.
  if (!contentAPI) {
    // If there is no content API, we cannot get any session data and consequently
    // cannot proceed with the app. This is a fatal error.
    throw new NoSessionDataError('No content API available', {
      ignoreUfoReporting: true,
    });
  }

  const { accountId, tenantId, activationId, publicLinkUserType } =
    contentAPI.sessionData;
  // To force entering anonymous mode, add ?anonymous=true to the URL
  if (new URL(globalThis.location.href).searchParams.has('anonymous')) {
    return {
      accountId: ANONYMOUS_ID,
      tenantId,
      activationId,
      publicLinkUserType: UserTypes.ANONYMOUS,
    };
  }

  return { accountId, tenantId, activationId, publicLinkUserType };
}

/**
 * gets session data: accountId, tenantId, and activationId
 * any data available here can be assumed to be available at app init
 *
 * @returns session data, or throws if it's not available
 */
export function getSessionData(): WhiteboardSessionData {
  const { accountId, tenantId, activationId, publicLinkUserType } =
    getSessionDataUnvalidated();

  const anonymousAccountId = accountId === ANONYMOUS_ID || !accountId;

  if (!tenantId) {
    throw new TenantIdMissingError('Tenant id missing from session data');
  }

  if (!activationId) {
    throw new ActivationIdMissingError('Activation id missing from session data');
  }

  if (anonymousAccountId) {
    return { accountId: ANONYMOUS_ID, tenantId, activationId, publicLinkUserType };
  }

  return { accountId, tenantId, activationId, publicLinkUserType };
}
